import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Col, Form, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type {
  SlantBillingSetupBody,
  SlantBillingWriteBackController,
} from "../../../api/src/slant/billing-write-back.controller";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import { useErrorMessage } from "../components/FormError";
import SubmitButton from "../components/SubmitButton";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import FirmNav from "./FirmNav";

/** One catalog row as `GET /slant/billing-fields/setup` returns it. */
export type SlantBillingFieldRow = UnpackResponse<
  SlantBillingWriteBackController["getSetup"]
>["data"]["fields"][number];

/**
 * One key the save is about to provision, tagged create or adopt, as
 * `POST /slant/billing-fields/setup/confirmation` returns it.
 */
export type SlantBillingFieldConfirmation = UnpackResponse<
  SlantBillingWriteBackController["confirmSetup"]
>["data"][number];

/** The parts of a row the page lets an administrator change. */
type FieldEdit = Pick<SlantBillingFieldRow, "fieldKey" | "displayName">;

/**
 * The shape a Slant field key has to take, mirroring the api's own guard
 * (`custom-field-definitions.service.ts`). Checked here so an administrator
 * sees the problem next to the input rather than as a failed save; the server
 * refuses these regardless.
 */
const SLANT_FIELD_KEY = /^[a-z][a-z0-9_]*$/;

/**
 * A row an administrator has left with no key and no label: "not this field".
 *
 * It is how a firm says it does not want one of the four, now that there is no
 * selection control (`an-decision-019fb9ae768b0da5`). The api stores nothing
 * for such a row, so the field stays unconfigured and every push skips it.
 */
function isBlank(row: { fieldKey: string; displayName: string }): boolean {
  return row.fieldKey.trim() === "" && row.displayName.trim() === "";
}

/**
 * What a failed save has to say, given which of the keys it was going to create
 * exist in Slant afterwards.
 *
 * The distinction matters more than the failure does: a definition that reached
 * Slant is permanent, so an administrator reading "save failed" and starting
 * over needs to know that the next save creates only what is still missing
 * rather than repeating the whole thing.
 */
function failedSaveMessage(created: string[], failed: string[]): string {
  if (created.length === 0) {
    return "The save failed and nothing was created in Slant. Save again to retry.";
  }
  if (failed.length === 0) {
    return `The save failed after ${created.join(", ")} ${
      created.length === 1 ? "was" : "were"
    } created in Slant. A field created in Slant cannot be removed through the API; only a firm administrator can remove it in Slant.`;
  }
  return `Some fields were created in Slant and some were not. Created in Slant: ${created.join(
    ", ",
  )}. Not created: ${failed.join(
    ", ",
  )}. A field created in Slant cannot be removed through the API, and saving again creates only the fields that are still missing.`;
}

/** What a successful save reports, naming the created and the adopted keys apart. */
function savedNotificationBody(created: string[], adopted: string[]): string {
  const parts: string[] = [];
  if (created.length > 0) {
    parts.push(`Created in Slant: ${created.join(", ")}.`);
  }
  if (adopted.length > 0) {
    parts.push(`Adopted in Slant: ${adopted.join(", ")}.`);
  }
  return parts.length === 0
    ? "The Slant billing field setup was saved."
    : parts.join(" ");
}

/**
 * The heading verb for a confirmation, so it never promises a create where
 * every key is adopted or the other way round.
 */
function provisionVerb(pending: SlantBillingFieldConfirmation[]): string {
  const creates = pending.some((field) => field.action === "create");
  const adopts = pending.some((field) => field.action === "adopt");
  if (creates && adopts) {
    return "Create and adopt";
  }
  return adopts ? "Adopt" : "Create";
}

/**
 * What the definition an adopt takes over actually is, in Slant's own words —
 * or `null` on a create, where there is no definition yet to describe.
 *
 * Shown on every adopt, not only a differing one, because that label and type
 * are what the field KEEPS: adopting issues no write, and Slant has no rename
 * or delete through the API. The rest of the line beside it is the
 * administrator's typed label and AdviceCloud's own type for the figure, so
 * without this the modal gating a permanent, unrenamable adopt describes
 * AdviceCloud's catalog rather than the thing being taken over.
 *
 * Reports, never blocks. Adoption matches on the field key alone, deliberately,
 * so that a save whose `POST` reached Slant but lost its response can recover
 * (`an-decision-019fb9aacb05839b`) — a difference here is something to read
 * before confirming, not a refusal. It says only what the two sides hold and
 * claims no consequence: what Slant does with a value written against a type it
 * did not expect is not established.
 *
 * The api decides what differs ({@link SlantBillingFieldConfirmation}); this
 * only words it. Same reason the page never derives create-versus-adopt: the
 * comparison is against Slant's definitions, which only the api can see.
 */
function adoptedDefinitionText(
  field: SlantBillingFieldConfirmation,
): string | null {
  const adopted = field.adopted;
  if (adopted === null) {
    return null;
  }

  const held = `In Slant: "${adopted.displayName}" (${adopted.fieldType}).`;
  const differences: string[] = [];
  if (adopted.displayNameDiffers) {
    differences.push(`the label entered here is "${field.displayName}"`);
  }
  if (adopted.fieldTypeDiffers) {
    differences.push(`the AdviceCloud figure is a ${field.fieldType}`);
  }
  if (differences.length === 0) {
    return held;
  }

  return `${held} That is not what this save carries: ${differences.join(
    ", and ",
  )}. Adopting takes the Slant definition as it stands — it cannot be renamed or retyped.`;
}

/** Whether an adopted definition differs from what this save carries. */
function adoptedDiffers(field: SlantBillingFieldConfirmation): boolean {
  return (
    field.adopted !== null &&
    (field.adopted.displayNameDiffers || field.adopted.fieldTypeDiffers)
  );
}

/**
 * A catalog row overlaid with the unsaved edits and the client-side validation
 * flags. The flags ride on the row rather than in an array the columns close
 * over, so the column definitions depend on `editField` alone — see `rows`.
 */
type SlantBillingFieldTableRowData = SlantBillingFieldRow & {
  /**
   * The label as the read returned it, before this page's edits — the firm's
   * saved label, or AdviceCloud's default where it has configured nothing.
   *
   * It names the row for a screen reader, and it has to hold still while the
   * label input is being typed into or emptied, so it cannot come from the
   * edits.
   */
  displayNameAsRead: string;
  /** Another row in this save carries the same field key. */
  duplicateKey: boolean;
  /** Not yet created, not blank, and the key is not a shape Slant uses. */
  malformedKey: boolean;
  /** Not yet created, not blank, and the label is blank. */
  blankLabel: boolean;
  /**
   * Already configured, and the administrator has emptied both inputs. The save
   * leaves it configured — un-configuring is not offered
   * (`an-decision-019fb9c35fc471ab`) — so the row says so rather than letting
   * the gesture look as though it worked.
   */
  emptiedButConfigured: boolean;
};

const columnHelper = createColumnHelper<SlantBillingFieldTableRowData>();

/**
 * The firm-level setup for the four AdviceCloud figures written onto a Slant
 * household: the field key and label Slant stores each one under.
 *
 * Readable by anybody in the firm — the state is worth seeing — but only an
 * administrator gets a save control, and the control is ABSENT rather than
 * disabled for everybody else, because saving is what creates the definitions
 * in Slant and a created definition is permanent.
 *
 * There is no selection control (`an-decision-019fb9ae768b0da5`). A field is
 * configured — key and label set and saved — or it is not, and an unconfigured
 * field is skipped on every push. Nor is there an un-configure control
 * (`an-decision-019fb9c35fc471ab`): configuration is one-way in this increment.
 *
 * Key and label are editable only while a field has not been created in Slant.
 * Slant has no rename and no delete through the API, so editing a created key
 * would leave the first definition in place and add a second one. Type is never
 * editable.
 *
 * A save that would provision anything is confirmed first, against the api's
 * own account of what it would provision and whether each key is created or
 * adopted. That account is asked for at the moment of the save, so it is taken
 * against the keys the administrator has actually typed.
 */
const SlantBillingFields = () => {
  const roles = useRoles();
  const isAdmin = roles.includes("Admin");

  const { isPending, isError, data, refetch } = useAuthenticatedFetch<
    UnpackResponse<SlantBillingWriteBackController["getSetup"]>
  >("/slant/billing-fields/setup");
  const setup = data?.data;
  const fields = setup?.fields;

  const confirmSetup = useAuthenticatedMutationAsync<
    UnpackResponse<SlantBillingWriteBackController["confirmSetup"]>
  >(
    "/slant/billing-fields/setup/confirmation",
    async (body: SlantBillingSetupBody) => ({
      method: "POST",
      body: JSON.stringify(body),
    }),
  );

  const saveSetup = useAuthenticatedMutationAsync<
    UnpackResponse<SlantBillingWriteBackController["saveSetup"]>
  >("/slant/billing-fields/setup", async (body: SlantBillingSetupBody) => ({
    method: "POST",
    body: JSON.stringify(body),
  }));

  const notificationContext = useContext(NotificationContext);
  const { setError, resetError, errorMessage } = useErrorMessage();
  const [isSaving, setIsSaving] = useState(false);
  // What the api says this save would provision, or `null` while there is
  // nothing to confirm. Never derived locally: create-versus-adopt is a fact
  // about Slant's definitions measured against the keys as typed, and only the
  // api can see both.
  const [pending, setPending] = useState<
    SlantBillingFieldConfirmation[] | null
  >(null);

  // The unsaved edits, keyed by `acField`. Seeded from the fetched rows and
  // re-seeded whenever they are re-read.
  const [edits, setEdits] = useState<Record<string, FieldEdit>>({});

  useEffect(() => {
    setEdits(
      Object.fromEntries(
        (fields ?? []).map((field): [string, FieldEdit] => [
          field.acField,
          {
            fieldKey: field.fieldKey,
            displayName: field.displayName,
          },
        ]),
      ),
    );
  }, [fields]);

  const editField = useCallback(
    (acField: string, change: Partial<FieldEdit>) =>
      setEdits((current) => ({
        ...current,
        [acField]: { ...current[acField], ...change },
      })),
    [],
  );

  // The fetched rows overlaid with the edits, in catalog order.
  const rows = useMemo(() => {
    const merged = (fields ?? []).map((field) => ({
      ...field,
      displayNameAsRead: field.displayName,
      ...edits[field.acField],
    }));
    const keyCounts = new Map<string, number>();
    for (const row of merged) {
      // A blank row names no field, so two of them do not share a key.
      if (isBlank(row)) continue;
      keyCounts.set(row.fieldKey, (keyCounts.get(row.fieldKey) ?? 0) + 1);
    }
    return merged.map((row) => {
      const blank = isBlank(row);
      return {
        ...row,
        // Carried on the row rather than read from an enclosing array, so the
        // column definitions stay dependent on `editField` alone. A fresh array
        // in their dependency list would rebuild every cell on each keystroke.
        duplicateKey: (keyCounts.get(row.fieldKey) ?? 0) > 1,
        malformedKey:
          !row.provisioned && !blank && !SLANT_FIELD_KEY.test(row.fieldKey),
        blankLabel: !row.provisioned && !blank && row.displayName.trim() === "",
        emptiedButConfigured: blank && row.configured,
      };
    });
  }, [fields, edits]);

  // Mirrors the api's guard (custom-field-definitions.service.ts). Client-side
  // only so the administrator sees the problem next to the input rather than as
  // a failed save; the server refuses these regardless.
  const hasInvalidRow = useMemo(
    () =>
      rows.some(
        (row) => row.malformedKey || row.blankLabel || row.duplicateKey,
      ),
    [rows],
  );

  // The body of both the confirmation and the save, so what an administrator
  // confirms cannot differ from what is then saved. `selected` is echoed back
  // untouched: it is still persisted, but nothing reads it as a gate
  // (`an-decision-019fb9ae768b0da5`).
  const savePayload = useMemo(
    (): SlantBillingSetupBody => ({
      fields: rows.map((row) => ({
        acField: row.acField,
        selected: row.selected,
        fieldKey: row.fieldKey,
        displayName: row.displayName,
      })),
    }),
    [rows],
  );

  const isDirty = useMemo(
    () =>
      (fields ?? []).some((field) => {
        const edit = edits[field.acField];
        return (
          edit !== undefined &&
          (edit.fieldKey !== field.fieldKey ||
            edit.displayName !== field.displayName)
        );
      }),
    [fields, edits],
  );

  // A save is worth offering when it would change something: an unsaved edit,
  // or a field carrying a key and a label with no Slant definition behind it —
  // AdviceCloud's suggestion the firm has not accepted yet, or a configured
  // field an earlier save did not manage to provision.
  const hasWorkToSave = useMemo(
    () => isDirty || rows.some((row) => !row.provisioned && !isBlank(row)),
    [isDirty, rows],
  );

  const submit = useCallback(
    async (provisioning: SlantBillingFieldConfirmation[]) => {
      setPending(null);
      setIsSaving(true);
      resetError();
      // Captured before the request: afterwards, the re-read state is the only
      // account of which of these actually reached Slant.
      const creating = provisioning
        .filter((field) => field.action === "create")
        .map((field) => field.fieldKey);
      const adopting = provisioning
        .filter((field) => field.action === "adopt")
        .map((field) => field.fieldKey);

      try {
        await saveSetup(savePayload);

        // Re-read rather than reuse the local edits: the provisioned rows come
        // back locked, with their Slant definition, without a page reload.
        await refetch();

        notificationContext.pushNotification({
          id: "slant-billing-fields-setup",
          header: "Billing Fields Saved",
          body: savedNotificationBody(creating, adopting),
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save the Slant billing field setup", err);

        // The failure may have created some of the definitions before it
        // stopped, and each of those is permanent. Re-read before saying
        // anything: the api's response carries no key names, so the re-read is
        // what distinguishes what was created from what was not. Adopted keys
        // are left out — recording an id again is not an irreversible act.
        const refreshed = (await refetch()).data?.data.fields ?? [];
        const created = creating.filter((key) =>
          refreshed.some((row) => row.fieldKey === key && row.provisioned),
        );
        setError(
          failedSaveMessage(
            created,
            creating.filter((key) => !created.includes(key)),
          ),
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      savePayload,
      saveSetup,
      refetch,
      notificationContext,
      setError,
      resetError,
    ],
  );

  const handleSave = useCallback(async () => {
    resetError();
    setIsSaving(true);
    try {
      // Ask the api what this save would provision, against the keys as typed.
      // Only a save that provisions something gets a human in front of it: a
      // save that provisions nothing cannot do anything irreversible, and
      // confirming it every time would teach an administrator to click through
      // the confirmation that can.
      const { data: provisioning } = await confirmSetup(savePayload);
      if (provisioning.length === 0) {
        await submit([]);
        return;
      }
      setPending(provisioning);
    } catch (err) {
      console.error(
        "Failed to read what the Slant billing field save would provision",
        err,
      );
      setError(
        "Could not work out which fields this save would create in Slant, so nothing was saved. Try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }, [confirmSetup, savePayload, submit, setError, resetError]);

  const columns = useMemo(
    () => [
      // Read-only on every row, unlike Key and Label, which the firm owns until
      // the definition is created. Once a firm has relabelled a field this is
      // the only text on the row that still says which AdviceCloud figure it
      // is — `acField` is the durable identity but it is a developer-facing
      // slug (`est_annual_fee`), so the name comes from the api, off the
      // SLANT_BILLING_FIELDS catalog. Deliberately not a lookup table here: the
      // catalog is the single source of truth for the set (the table carries no
      // CHECK constraint on `ac_field` for exactly that reason, V144), and a
      // second copy in the client would fork it.
      columnHelper.accessor((row) => row.acFieldName, {
        id: "acFieldName",
        header: () => "AdviceCloud Field",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.fieldKey, {
        id: "fieldKey",
        header: () => "Key",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <>
            {row.original.provisioned ? (
              row.original.fieldKey
            ) : (
              <Form.Control
                type="text"
                value={row.original.fieldKey}
                aria-label={`Field key for ${row.original.displayNameAsRead}`}
                onChange={(e) =>
                  editField(row.original.acField, { fieldKey: e.target.value })
                }
              />
            )}
            {row.original.malformedKey ? (
              <div className="text-danger small mt-1">
                Use lower-case letters, digits and underscores, starting with a
                letter. A Slant custom field cannot be renamed or removed once
                created.
              </div>
            ) : null}
            {row.original.duplicateKey ? (
              <div className="text-danger small mt-1">
                Two fields cannot share the key {row.original.fieldKey}. Each
                figure needs its own Slant custom field.
              </div>
            ) : null}
          </>
        ),
      }),
      columnHelper.accessor((row) => row.displayName, {
        id: "displayName",
        header: () => "Label",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.provisioned ? (
            row.original.displayName
          ) : (
            <>
              <Form.Control
                type="text"
                value={row.original.displayName}
                aria-label={`Label for ${row.original.displayNameAsRead}`}
                onChange={(e) =>
                  editField(row.original.acField, {
                    displayName: e.target.value,
                  })
                }
              />
              {row.original.blankLabel ? (
                <div className="text-danger small mt-1">
                  A label is required. It is what advisors see on the Slant
                  record, and it cannot be changed once the field is created.
                </div>
              ) : null}
              {row.original.emptiedButConfigured ? (
                <div className="text-warning small mt-1">
                  This field is already configured. Clearing the key and label
                  here does not stop AdviceCloud writing it.
                </div>
              ) : null}
            </>
          ),
      }),
      columnHelper.accessor((row) => row.fieldType, {
        id: "fieldType",
        header: () => "Type",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "state",
        header: () => "State",
        cell: ({ row }) =>
          row.original.provisioned
            ? "Created"
            : row.original.configured
              ? "Configured, not created"
              : "Not configured",
      }),
    ],
    [editField],
  );

  const { table } = useTable({ columns, data: rows, enableSorting: false });

  if (isPending) {
    return (
      <TabContainerWithTabs tabs={FirmNav}>
        <Loading />
      </TabContainerWithTabs>
    );
  }

  // A failed read renders nothing but the failure. An empty list here would
  // read as "no fields are set up" and invite an administrator to create
  // definitions that already exist — which cannot then be removed.
  //
  // This branch stays ahead of the not-connected one below and keeps its own
  // wording: a user who DOES have a PAT and whose Slant read failed has to be
  // told the read failed, not told to connect something they already have.
  if (isError) {
    return (
      <TabContainerWithTabs tabs={FirmNav}>
        <Alert variant="danger">
          Failed to read the Slant custom field setup. The fields are not listed
          because the read failed, not because they are missing from Slant.
        </Alert>
      </TabContainerWithTabs>
    );
  }

  // Hiding the tab does not stop the URL, so the page answers for itself
  // (APP-1437). The api reports `connected: false` for a user with no Slant
  // Personal Access Token and reads nothing — no rows, and nothing seeded —
  // which is a different thing from the failed read above, and rendered as a
  // different thing. No save control exists on this branch at all: there is
  // nothing to save, and saving is what creates the permanent definitions.
  //
  // Not a redirect. The server is the gate and the page states the refusal,
  // the way the rest of the app does; a redirect would take the reason with it.
  if (setup?.connected === false) {
    return (
      <TabContainerWithTabs tabs={FirmNav}>
        <Row>
          <Col>
            <h4 className="mt-2">Billing Fields</h4>
            <Alert variant="info">
              Connect Slant to configure billing fields. AdviceCloud reads and
              creates these Slant custom fields with your own Slant Personal
              Access Token, so this page needs one — your firm&rsquo;s setup is
              not enough on its own. Connect Slant on your{" "}
              <Link to="/profile">profile</Link>.
            </Alert>
          </Col>
        </Row>
      </TabContainerWithTabs>
    );
  }

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      <Row>
        <Col>
          <h4 className="mt-2">Billing Fields</h4>
          <p>
            AdviceCloud writes four fields onto the household record in Slant.
            Set the key and label Slant stores each one under, and save. A saved
            field is written on every push; one left with no key and no label is
            skipped.
          </p>
          <p>
            The Slant field is created the first time a save carries the
            AdviceCloud field, and a key Slant already has is adopted rather
            than created. After that its key, label and type are fixed: Slant
            has no rename, and a custom field cannot be removed through the API.
            Clearing a saved field here does not stop AdviceCloud writing it.
          </p>
          {isAdmin ? null : (
            <p className="text-muted">
              An administrator configures these fields and creates them in
              Slant.
            </p>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <Table table={table} disablePagination showSearchGroup={false} />
        </Col>
      </Row>
      {/*
        Administrator only, and ABSENT rather than disabled for everybody else:
        a save is what creates the definitions in Slant, so a non-administrator
        session must have no control that can reach it at all. The confirmation
        hangs off the same control for the same reason.
      */}
      {isAdmin ? (
        <>
          <Row>
            <Col>
              {errorMessage}
              <SubmitButton
                type="button"
                label="Save"
                isSubmitting={isSaving}
                disabled={!hasWorkToSave || isSaving || hasInvalidRow}
                onClick={handleSave}
              />
            </Col>
          </Row>
          <Modal
            show={pending !== null}
            onHide={() => setPending(null)}
            aria-labelledby="slant-billing-fields-confirm-title"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title id="slant-billing-fields-confirm-title">
                {provisionVerb(pending ?? [])} fields in Slant
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Saving provisions{" "}
                {(pending ?? []).length === 1 ? "this field" : "these fields"}{" "}
                in Slant:
              </p>
              <ul>
                {(pending ?? []).map((field) => (
                  <li key={field.acField}>
                    {/*
                      The key, the label and the outcome stay in one element so
                      the line reads as one sentence; the adopted definition
                      goes beneath it rather than inside it, because it is
                      Slant's account of the field and the line above is
                      AdviceCloud's.
                    */}
                    <span>
                      {field.fieldKey} — {field.displayName} —{" "}
                      {field.action === "adopt"
                        ? "already exists in Slant and will be adopted"
                        : "will be created"}
                    </span>
                    {field.adopted !== null ? (
                      <div
                        className={
                          adoptedDiffers(field)
                            ? // `text-danger`, not `text-warning`: Bootstrap's
                              // warning colour is 1.63:1 on white, far short of
                              // WCAG AA's 4.5:1, and APP-1451 (ed840936) already
                              // had to darken 13-F badge text for exactly that.
                              // `text-danger` is 4.53:1 and is what this file
                              // already uses for an inline caution.
                              "text-danger small"
                            : "text-muted small"
                        }
                      >
                        {adoptedDefinitionText(field)}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {(pending ?? []).some((field) => field.action === "create") ? (
                <p>
                  A field created in Slant cannot be renamed or removed through
                  the API. Only a firm administrator can remove it, in Slant.
                </p>
              ) : null}
              {(pending ?? []).some((field) => field.action === "adopt") ? (
                <p>
                  An adopted field already exists in Slant and may already hold
                  this firm&apos;s own data. AdviceCloud takes it over and
                  overwrites its value on every household it pushes.
                </p>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              <ActionButton
                variant="secondary"
                label="Cancel"
                onClick={() => setPending(null)}
                disabled={isSaving}
              />
              <SubmitButton
                type="button"
                label={`${provisionVerb(pending ?? [])} in Slant`}
                isSubmitting={isSaving}
                onClick={() => submit(pending ?? [])}
              />
            </Modal.Footer>
          </Modal>
        </>
      ) : null}
    </TabContainerWithTabs>
  );
};

export default SlantBillingFields;
